/**
 * Web-STOMP client for direct RabbitMQ messaging.
 *
 * Connects to RabbitMQ via Web-STOMP (rabbitmq_web_stomp plugin).
 * Uses JWT for auth. Subscribe to AMQP routing keys via /exchange/fapi_events/<routing_key>.
 *
 * @see docs/AMQP.md §0 Client Connectivity
 */

/* eslint-env es2022 */


!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{},n=(new e.Error).stack;n&&(e._sentryDebugIds=e._sentryDebugIds||{},e._sentryDebugIds[n]="8f449e53-067e-541f-9d37-d8020d4eb934")}catch(e){}}();
const MESSAGING_EXCHANGE = "fapi_events";
const STOMP_PORT = 15674;
const RECONNECT_DELAY_MS = 5000;
/** Min time between JWT refresh attempts triggered by broker auth errors (avoid refresh storms). */
const STOMP_AUTH_DENIED_REFRESH_MIN_INTERVAL_MS = 60_000;
const WEB_SOCKET_OPEN = 1;
const WEB_SOCKET_CLOSING = 2;
const WEB_SOCKET_CLOSED = 3;
const WEB_SOCKET_READY_STATE_LABELS = {
    0: "CONNECTING",
    [WEB_SOCKET_OPEN]: "OPEN",
    [WEB_SOCKET_CLOSING]: "CLOSING",
    [WEB_SOCKET_CLOSED]: "CLOSED",
};

let lastStompAuthDeniedRefreshAtMs = 0;

/**
 * Get Web-STOMP broker URL. Uses site.messaging_stomp_broker_url if set,
 * else derives from FAPI base URL.
 * Production: wss://api.parkranger.services/ws-stomp (nginx TLS proxy on 443).
 * Local: ws://localhost:15674/ws (direct to RabbitMQ).
 * @returns {string}
 */
function getStompBrokerUrl() {
    const site = (typeof window !== "undefined" && window.site) || (typeof self !== "undefined" && self.site) || {};
    if (site.messaging_stomp_broker_url) {
        return site.messaging_stomp_broker_url;
    }
    const host = typeof window !== "undefined" ? window.location?.hostname || "" : "";
    if (
        host === "localhost"
        || host === "127.0.0.1"
        || host.endsWith(".localhost")
        || host.endsWith(".localhost.blue-labs.org")
    ) {
        return `ws://127.0.0.1:${STOMP_PORT}/ws`;
    }
    const base = site.fapi_base_url || "https://api.parkranger.services";
    const u = new URL(base);
    const protocol = u.protocol === "https:" ? "wss" : "ws";
    return `${protocol}://${u.hostname}/ws-stomp`;
}

/**
 * Get STOMP vhost (CONNECT host header). FAPI/Django use parkranger.services vhost.
 * Local: / (default). Production: parkranger.services.
 * @returns {string}
 */
function getStompVhost() {
    const site = (typeof window !== "undefined" && window.site) || (typeof self !== "undefined" && self.site) || {};
    if (site.messaging_stomp_vhost) {
        return site.messaging_stomp_vhost;
    }
    const host = typeof window !== "undefined" ? window.location?.hostname || "" : "";
    if (
        host === "localhost"
        || host === "127.0.0.1"
        || host.endsWith(".localhost")
        || host.endsWith(".localhost.blue-labs.org")
    ) {
        return "parkranger";
    }
    return "parkranger.services";
}

/**
 * Return the native WebSocket behind a StompJs client when it is available.
 * StompJs exposes this differently across builds, so check the public property
 * first and then the current handler field used by the browser bundle.
 *
 * @param {import('@stomp/stompjs').Client | null | undefined} client
 * @returns {WebSocket | null}
 */
function getMessagingNativeWebSocket(client) {
    return client?.webSocket || client?._stompHandler?._webSocket || null;
}

/**
 * @param {import('@stomp/stompjs').Client | null | undefined} client
 * @returns {{readyState: number|null, label: string, connected: boolean}}
 */
export function getMessagingSocketState(client) {
    const socket = getMessagingNativeWebSocket(client);
    const readyState = typeof socket?.readyState === "number" ? socket.readyState : null;
    return {
        readyState,
        label: readyState === null ? "UNKNOWN" : (WEB_SOCKET_READY_STATE_LABELS[readyState] || `STATE_${readyState}`),
        connected: Boolean(client?.connected),
    };
}

/**
 * True only when STOMP considers the session connected and the native socket is
 * open. Use before publish/subscribe calls that would send on the socket.
 *
 * @param {import('@stomp/stompjs').Client | null | undefined} client
 * @returns {boolean}
 */
export function isMessagingClientUsable(client) {
    if (!client?.connected) {
        return false;
    }
    const state = getMessagingSocketState(client);
    return state.readyState === null || state.readyState === WEB_SOCKET_OPEN;
}

/**
 * Deactivate without asking StompJs to send DISCONNECT on an already-closing
 * socket, which otherwise creates noisy "WebSocket is already CLOSED" errors.
 *
 * @param {import('@stomp/stompjs').Client | null | undefined} client
 * @returns {Promise<void>}
 */
export async function safeDeactivateMessagingClient(client) {
    if (!client?.deactivate) {
        return;
    }
    const state = getMessagingSocketState(client);
    const force = state.readyState === WEB_SOCKET_CLOSING || state.readyState === WEB_SOCKET_CLOSED;
    try {
        await client.deactivate(force ? { force: true } : undefined);
    } catch (_err) {
        // Best-effort teardown; callers are usually already handling reconnect.
    }
}

import { fetchAndUpdateFapiToken } from '/static/js/fapi-token.js';

/**
 * Fetch fresh JWT from Django. Updates window.__FAPI_JWT and schedules proactive refresh.
 * @returns {Promise<string|null>}
 */
async function fetchFreshMessagingJwt() {
    const token = await fetchAndUpdateFapiToken();
    return token;
}

/**
 * RabbitMQ Web-STOMP / OAuth often surfaces missing scope as "access refused" or similar.
 * @param {string} text
 * @returns {boolean}
 */
function messageSuggestsRabbitAuthFailure(text) {
    const s = String(text || "").toLowerCase();
    if (!s) {
        return false;
    }
    return (
        s.includes("access refused")
        || s.includes("access_refused")
        || s.includes("access denied")
        || s.includes("not_authorized")
        || s.includes("vhost_access_denied")
        || s.includes("login refused")
        || s.includes("bad connect")
    );
}

/**
 * Fetch a new JWT after the broker hints the current token/scopes are insufficient.
 * Throttled to at most one attempt per {@link STOMP_AUTH_DENIED_REFRESH_MIN_INTERVAL_MS}.
 * @returns {Promise<boolean>} True if a refresh was attempted and returned a token.
 */
async function requestJwtRefreshForMessagingAuthDenied() {
    const now = Date.now();
    if (now - lastStompAuthDeniedRefreshAtMs < STOMP_AUTH_DENIED_REFRESH_MIN_INTERVAL_MS) {
        console.debug("Messaging: JWT refresh skipped (throttled after broker auth hint)");
        return false;
    }
    lastStompAuthDeniedRefreshAtMs = now;
    const token = await fetchFreshMessagingJwt();
    if (!token) {
        console.warn("Messaging: broker auth hint but token refresh failed");
    }
    return Boolean(token);
}

/** Buffer before expiry (seconds) to refresh proactively. */
const JWT_EXPIRY_BUFFER_SEC = 300;

function decodeJwtClaimsUnsafe(token) {
    const value = String(token || "").trim();
    if (!value) return null;
    const parts = value.split(".");
    if (parts.length < 2) return null;
    try {
        const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
        const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
        return JSON.parse(atob(padded));
    } catch (_err) {
        return null;
    }
}

/**
 * Get JWT for STOMP auth. Uses window.__FAPI_JWT or fetches from /tools/api/auth/fapi-token/.
 * Treats token as expired when __FAPI_JWT_EXPIRES is in the past or within buffer.
 * @param {boolean} [forceRefresh] - If true, always fetch fresh token (e.g. after Bad CONNECT)
 * @returns {Promise<string>}
 */
async function getMessagingJwt(forceRefresh = false) {
    const now = Math.floor(Date.now() / 1000);
    const expiresAt = (typeof window !== "undefined" ? window.__FAPI_JWT_EXPIRES : 0) || 0;
    if (!forceRefresh && typeof window !== "undefined" && window.__FAPI_JWT) {
        if (expiresAt > now + JWT_EXPIRY_BUFFER_SEC) {
            return window.__FAPI_JWT;
        }
    }
    const token = await fetchFreshMessagingJwt();
    if (token) return token;
    throw new Error("Could not obtain FAPI JWT for messaging");
}

/**
 * Create and configure a STOMP client. Does not connect.
 * Call client.activate() to connect.
 *
 * @param {Object} [opts]
 * @param {string} [opts.brokerURL] - Override broker URL
 * @param {boolean} [opts.debug] - Enable STOMP debug logging
 * @returns {Promise<{client: import('@stomp/stompjs').Client, jwt: string}>}
 */
export async function createMessagingClient(opts = {}) {
    const StompJs = typeof window !== "undefined" && window.StompJs;
    if (!StompJs?.Client) {
        throw new Error("StompJs.Client not loaded. Ensure @stomp/stompjs script is present.");
    }

    const forceRefresh = opts._forceRefresh === true;
    const jwt = await getMessagingJwt(forceRefresh);
    const claims = decodeJwtClaimsUnsafe(jwt) || {};
    console.debug("Messaging JWT claims", {
        sub: claims.sub || null,
        aud: claims.aud || null,
        scope: claims.scope || null,
        scopes: claims.scopes || null,
        exp: claims.exp || null,
    });
    const brokerURL = opts.brokerURL ?? getStompBrokerUrl();

    const client = new StompJs.Client({
        brokerURL,
        connectHeaders: {
            login: "jwt",
            passcode: jwt,
            host: getStompVhost(),
        },
        debug: opts.debug ? (str) => console.log("STOMP:", str) : () => {},
        // Disable StompJs auto-reconnect: it reuses the same (possibly expired) JWT.
        // Message monitor handles reconnect via onDisconnect, creating a new client with fresh JWT.
        reconnectDelay: 0,
        // 60 s outgoing heartbeat — background tabs throttle timers to ~1/min
        // but RabbitMQ tolerates 2 missed heartbeats (120 s window), so a 60 s
        // interval survives worst-case browser throttling.  The default 10 s
        // does not: the server kills the socket after 20 s of silence, which
        // triggers a reconnect loop that spams inbox requests.
        heartbeatOutgoing: 60000,
        heartbeatIncoming: 10000,
    });

    return { client, jwt };
}

/**
 * Extract routing key from STOMP message destination.
 * Destination format: /exchange/fapi_events/<routing_key>
 * @param {string} [dest]
 * @returns {string}
 */
function routingKeyFromDestination(dest) {
    if (!dest) return "";
    const prefix = `/exchange/${MESSAGING_EXCHANGE}/`;
    return dest.startsWith(prefix) ? dest.slice(prefix.length) : dest;
}

/**
 * Run subscriber on a new macrotask so many STOMP frames cannot starve the main thread
 * (input, paint, other timers) in a single turn.
 *
 * @param {object} message - Raw STOMP frame body from StompJs
 * @param {(normalized: object) => void} callback
 */
function scheduleAmqpSubscriberDispatch(message, callback) {
    setTimeout(() => {
        try {
            const body = message.body ? JSON.parse(message.body) : {};
            const dest = message.destination ?? message.headers?.destination ?? "";
            const routing_key = routingKeyFromDestination(dest) || body.routing_key || "";
            const normalized = {
                source: "amqp",
                routing_key,
                ...body,
            };
            callback(normalized);
        } catch {
            callback({ source: "amqp", routing_key: "", ...message });
        }
    }, 0);
}

/**
 * Subscribe to an AMQP routing key on the fapi_events exchange.
 * Destination format: /exchange/fapi_events/<routing_key>
 *
 * Callback receives normalized message: { source, routing_key, event_id, timestamp, topic, action, payload }
 * (matches format from former FAPI WebSocket relay for compatibility)
 *
 * @param {import('@stomp/stompjs').Client} client - Connected STOMP client
 * @param {string} routingKey - e.g. "pr.background.server.presence", "pr.payment.#", "#"
 * @param {(message: object) => void} callback - Receives normalized { source, routing_key, ...body }
 * @returns {import('@stomp/stompjs').StompSubscription}
 */
export function subscribeToRoutingKey(client, routingKey, callback) {
    if (!isMessagingClientUsable(client)) {
        const state = getMessagingSocketState(client);
        throw new Error(`STOMP client is not open for subscribe (${state.label})`);
    }
    const destination = `/exchange/${MESSAGING_EXCHANGE}/${routingKey}`;
    return client.subscribe(destination, (message) => {
        scheduleAmqpSubscriberDispatch(message, callback);
    });
}

/**
 * Publish a JSON payload to an AMQP routing key on the fapi_events exchange.
 *
 * @param {import('@stomp/stompjs').Client} client - Connected STOMP client
 * @param {string} routingKey - e.g. "pr.chat.inbox.request"
 * @param {object} payload - JSON-serializable payload
 */
export function publishToRoutingKey(client, routingKey, payload = {}) {
    if (!isMessagingClientUsable(client)) {
        const state = getMessagingSocketState(client);
        throw new Error(`STOMP client is not open for publish (${state.label})`);
    }
    const destination = `/exchange/${MESSAGING_EXCHANGE}/${routingKey}`;
    client.publish({
        destination,
        headers: {
            "content-type": "application/json",
        },
        body: JSON.stringify(payload || {}),
    });
}

/**
 * Connect to RabbitMQ Web-STOMP and return the client.
 * Resolves when connected. On Bad CONNECT (e.g. stale JWT, vhost denied),
 * fetches fresh token and retries once.
 *
 * @param {Object} [opts]
 * @param {string} [opts.brokerURL]
 * @param {boolean} [opts.debug]
 * @returns {Promise<import('@stomp/stompjs').Client>}
 */
export async function connectMessaging(opts = {}) {
    async function tryConnect(forceRefresh = false) {
        const { client } = await createMessagingClient({ ...opts, _forceRefresh: forceRefresh });
        let brokerHost = null;
        try {
            brokerHost = new URL(String(client.brokerURL || '')).host;
        } catch (_e) {
            brokerHost = null;
        }
        console.log('[patrol-debug#6] STOMP tryConnect', { brokerHost, forceRefresh });
        return new Promise((resolve, reject) => {
            let connectCompleted = false;

            async function onPostConnectAuthDeniedHint() {
                const refreshed = await requestJwtRefreshForMessagingAuthDenied();
                if (refreshed) {
                    await safeDeactivateMessagingClient(client);
                }
            }

            client.onConnect = () => {
                connectCompleted = true;
                console.log('[patrol-debug#6] STOMP onConnect');
                resolve(client);
            };
            client.onStompError = (frame) => {
                const headerMsg = frame.headers?.message || "";
                const bodyMsg = String(frame.body || "");
                const combined = `${headerMsg} ${bodyMsg}`.trim();
                const msg = combined || "Unknown";
                console.log('[patrol-debug#6] STOMP onStompError', { msg, connectCompleted });
                console.warn("STOMP error:", msg, "headers:", frame.headers, "body:", frame.body);
                if (messageSuggestsRabbitAuthFailure(msg) && connectCompleted) {
                    void onPostConnectAuthDeniedHint();
                }
                if (!connectCompleted) {
                    reject({ frame, msg });
                }
            };
            client.onWebSocketClose = (event) => {
                if (!connectCompleted) {
                    console.log('[patrol-debug#6] STOMP WebSocketClose before CONNECT', {
                        code: event?.code,
                        reason: event?.reason,
                    });
                    reject({
                        msg: `WebSocket closed before STOMP CONNECTED (${event?.code || "unknown"})`,
                    });
                }
            };
            client.onWebSocketError = () => {
                if (!connectCompleted) {
                    console.log('[patrol-debug#6] STOMP WebSocketError before CONNECT');
                    reject({ msg: "WebSocket error before STOMP CONNECTED" });
                }
            };
            client.activate();
        });
    }

    try {
        return await tryConnect(false);
    } catch (err) {
        const headerLine = String(err?.frame?.headers?.message || err?.msg || "");
        const bodyLine = String(err?.frame?.body || "");
        const isBadConnect = headerLine.includes("Bad CONNECT") || bodyLine.includes("Bad CONNECT");
        const authHint = isBadConnect || messageSuggestsRabbitAuthFailure(headerLine) || messageSuggestsRabbitAuthFailure(bodyLine);
        if (authHint) {
            const refreshed = await requestJwtRefreshForMessagingAuthDenied();
            if (refreshed) {
                console.warn("Messaging: retrying with fresh JWT after connect auth error");
                return tryConnect(true);
            }
        }
        throw new Error(err?.msg ? `STOMP error: ${err.msg}` : "STOMP connection failed");
    }
}

export {
    MESSAGING_EXCHANGE,
    STOMP_AUTH_DENIED_REFRESH_MIN_INTERVAL_MS,
    RECONNECT_DELAY_MS,
    getStompBrokerUrl,
    getStompVhost,
    getMessagingJwt,
};

// Expose on window for non-module consumers
if (typeof window !== "undefined") {
    window.messaging = {
        connectMessaging,
        createMessagingClient,
        subscribeToRoutingKey,
        publishToRoutingKey,
        getMessagingSocketState,
        isMessagingClientUsable,
        safeDeactivateMessagingClient,
        MESSAGING_EXCHANGE,
        STOMP_AUTH_DENIED_REFRESH_MIN_INTERVAL_MS,
        RECONNECT_DELAY_MS,
        getStompBrokerUrl,
        getStompVhost,
        getMessagingJwt,
    };
}

//# debugId=8f449e53-067e-541f-9d37-d8020d4eb934
